<template>
  <div
    :class="componentClass"
    :style="componentStyle"
  >
    <label
      :class="labelClass"
      :for="sluggedLabel"
    >
      {{ label }}
    </label>
    <div
      :class="['select-custom', { 'with-icon-before position-relative': hasIcon }]"
      :aria-label="componentTooltip"
      data-balloon-pos="up"
    >
      <font-awesome-icon
        v-if="hasIcon"
        :icon="$v7[iconStyle][iconName]"
        class="select-custom-icon position-absolute"
      />

      <select
        :id="sluggedLabel"
        :class="selectClass"
        :style="selectStyle"
        :disabled="isDisabled"
        :required="isRequired"
        @blur="onBlur"
        @focus="$emit('focus')"
        @input="$emit('change', $event.target.value)"
      >
        <slot name="options">
          <option
            v-for="option in options"
            :key="option.name"
            :value="option.value"
            :selected="option.value === value ? 'selected' : null"
            :disabled="option.disabled === true ? 'disabled' : null"
          >
            {{ option[optionLabelProp] }}
          </option>
        </slot>
      </select>
    </div>

    <slot name="tip">
      <p
        v-if="tip"
        class="tip margin-top-2xs margin-bottom-0"
      >
        {{ tip }}
      </p>
    </slot>
  </div>
</template>

<script>
  import sluggify from '../../utils/sluggify'

  export default {
    name: 'SelectCustom',

    inheritAttrs: false,

    props: {
      customSelectStyle: {
        type: Object,
        default: () => {}
      },
      customStyle: {
        type: Object,
        default: () => {}
      },
      iconName: {
        type: String,
        default: null
      },
      iconStyle: {
        type: String,
        default: 'regular'
      },
      isDisabled: {
        type: Boolean,
        default: false
      },
      isDisabledLabel: {
        type: String,
        default: 'Unavailable!'
      },
      isFullWidth: {
        type: Boolean,
        default: true
      },
      isMinimal: {
        type: Boolean,
        default: false
      },
      isRequired: {
        type: Boolean,
        default: true
      },
      label: {
        type: String,
        required: true
      },
      selectMinWidth: {
        type: String,
        default: null
      },
      options: {
        type: Array,
        default: null
      },
      optionLabelProp: {
        type: String,
        default: 'name'
      },
      selectWhitespace: {
        type: String,
        default: '4xl'
      },
      tip: {
        type: String
      },
      value: {
        type: String,
        default: null
      }
    },

    computed: {
      componentClass() {
        return [`select-custom field field-${this.sluggedLabel}`, { 'disabled is-disabled': this.isDisabled }]
      },

      componentStyle() {
        let style = {}

        if (this.isDisabled) {
          style['--input-disabled-color'] = 'var(--fa-gravy)'
        }

        return {
          ...style,
          ...this.customStyle
        }
      },

      componentTooltip() {
        if (this.isMinimal) {
          return this.isDisabled ? this.isDisabledLabel : this.label
        } else return null
      },

      hasIcon() {
        return this.iconName
      },

      labelClass() {
        return ['margin-bottom-2xs', { 'sr-only': this.isMinimal }]
      },

      selectClass() {
        return [`select-custom-select select-custom-${this.sluggedLabel} margin-bottom-0 padding-right-${this.selectWhitespace}`]
      },

      selectStyle() {
        let style = {}

        style['font-weight'] = 'var(--input-font-weight)'
        style['position'] = 'static'
        style['--input-box-shadow'] = 'none'
        style['--input-min-height'] = 'auto'

        if (this.isFullWidth) {
          style['width'] = '100%'
        } else {
          style['width'] = 'auto'
        }

        if (this.selectMinWidth) {
          style['min-width'] = this.selectMinWidth
        }

        if (this.hasIcon) {
          style['padding-left'] = 'calc((var(--input-padding-horizontal) - var(--input-border-width)) * 1.6 + var(--input-with-icon-scale) * 1em)'
        }

        return {
          ...style,
          ...this.customSelectStyle
        }
      },

      sluggedLabel() {
        return sluggify(this.label)
      }
    },

    methods: {
      onBlur(event) {
        this.$emit('blur')
        this.updateValue(event)
      },

      updateValue(event) {
        this.$emit('input', {
          valid: event.target.validity.valid,
          value: event.target.value
        })

        this.$emit('validity', event.target.validity)
      },

      focus() {
        try {
          this.$refs.field.focus()
        } catch (e) {
          // noop
        }
      }
    }
  }
</script>
